// AdminDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "./firebase";
import {
  doc, getDoc, getDocs, collection, updateDoc, deleteDoc,
  query, where, orderBy, limit, getCountFromServer, collectionGroup, serverTimestamp,setDoc,  
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
// AdminDashboard.jsx
import AdminPromoManager from "./AdminPromoManager"; // ⚠️ rregullo path-in sipas strukturës tënde
import AdminStatsPanel from "./AdminStatsPanel";
import AdminSpeciality from "./AdminSpecialtiesPage";
import {
  FileText,
  CheckCircle,
  XCircle,
  Users,
  Clock,
  Shield,
  Trash2,
  Search,
  Stethoscope,
  MapPin,
  Phone,
  Award,
  Calendar,
  ExternalLink,
  Download,
  BarChart2,
  Activity,
  DollarSign,
  LogOut,
  RefreshCcw,
  AlertTriangle,
  Megaphone,
  Image as ImageIcon,
} from "lucide-react";
import { signOut } from "firebase/auth";

/* ====================== Helpers ====================== */
const fmtBytes = (bytes = 0) => {
  if (!bytes) return "0 B";
  const units = ["B","KB","MB","GB","TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};
const safeLower = (s) => (s || "").toString().toLowerCase();
const fmtCurrency = (n, currency = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(n || 0));
const getPrice = (a) => {
  const v = a.servicePrice ?? a.price ?? a.amount ?? 0;
  const num = typeof v === "string" ? Number(v.replace(/[^\d.]/g, "")) : Number(v);
  return isNaN(num) ? 0 : num;
};

const downloadCSV = (rows, filename) => {
  if (!rows?.length) return;
  const cols = Object.keys(rows[0]);
  const csv = [
    cols.join(","),
    ...rows.map(r => cols.map(c => {
      const v = r[c] ?? "";
      const needsQuotes = /[,"\n]/.test(String(v));
      const escaped = String(v).replace(/"/g, '""');
      return needsQuotes ? `"${escaped}"` : escaped;
    }).join(","))
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

/* ===== Rank helpers: mirror backend + sponsored override ===== */
/* ===== Rank helpers: mirror backend + sponsored override ===== */
const clamp01 = (x) => Math.max(0, Math.min(1, Number(x) || 0));
function logNorm(value, cap) {
  const v = Math.max(0, Number(value) || 0);
  const C = Math.max(1, Number(cap) || 1);
  return Math.min(1, Math.log1p(v) / Math.log1p(C));
}

// UI-only explainer that mirrors your Cloud Function computeDoctorRankScore()
// Returns { score, parts, norm }
function explainServerRank(d = {}) {
  const ratingAvg           = Number(d.ratingAvg ?? 0);               // 0..5
  const completedAppts      = Number(d.stats?.completedAppts ?? 0);
  const totalAppts          = Number(d.stats?.totalAppts ?? 0);
  const researchEngagements = Number(d.stats?.researchEngagements ?? 0);
  const years               = Number(d.experienceYears ?? 0);
  const profilePct          = Number(d.profileCompletePct ?? 0);      // 0..1
  const verified            = !!d.verified;
  const followers           = Number(d.followerCount ?? 0);

  // Normalizations (0..1)
  const R_rating   = clamp01(ratingAvg / 5);
  const R_completed= logNorm(completedAppts, 100);
  const R_research = logNorm(researchEngagements, 1000);
  const R_years    = clamp01(Math.min(25, Math.max(0, years)) / 20);
  const R_profile  = clamp01(profilePct);
  const R_total    = logNorm(totalAppts, 200);
  const R_verified = verified ? 1 : 0;
  const R_follow   = logNorm(followers, 5000);

  // Others 15% split → verified (10%) + followers (5%)
  const verifiedPart  = 0.10 * R_verified;
  const followersPart = 0.05 * R_follow;
  const R_others = clamp01((verifiedPart + followersPart) / 0.15) * 0.15;

  const W = { rating:0.30, completed:0.10, research:0.05, years:0.10, profile:0.10, total:0.20, others:0.15 };

  const parts = {
    rating:    R_rating    * W.rating,
    completed: R_completed * W.completed,
    research:  R_research  * W.research,
    years:     R_years     * W.years,
    profile:   R_profile   * W.profile,
    total:     R_total     * W.total,
    others:    R_others,
    verifiedPart, followersPart
  };

  const score01 = parts.rating + parts.completed + parts.research + parts.years + parts.profile + parts.total + parts.others;
  const score = Math.round(score01 * 10000) / 100; // 0..100, 2 decimals
  return { score, parts, norm: { R_rating, R_completed, R_research, R_years, R_profile, R_total, R_verified, R_follow } };
}

// Which score do we show/sort by?
const effectiveScore = (d) => {
  if (typeof d?.paraRankscore === "number") return Number(d.paraRankscore);
  if (typeof d?.rankScore === "number") return Number(d.rankScore);
  return explainServerRank(d).score; // fallback if rankScore missing
};

/* ====================== Component ====================== */
const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);
// Service Keyword Mapper
const [kwRows, setKwRows] = useState([]); // [{key, doctorId, doctorName, specialty, serviceIndex, title, price, keywords:[]}]
const [kwSearch, setKwSearch] = useState("");
const [kwDirty, setKwDirty] = useState(new Set()); // keys marked as edited

  // Verification queues
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);

  // Doctors collection (loaded for ranking + reconciliation)
  const [doctorsById, setDoctorsById] = useState({}); // { [uid]: doctorDocData }
  const [recon, setRecon] = useState({ approvedNotInDoctors: [], doctorsNotVerified: [] });
const toNumber = (v) => {
  const num = typeof v === "string" ? Number(v.replace(/[^\d.,-]/g, "").replace(",", ".")) : Number(v);
  return isNaN(num) ? 0 : num;
};
const viewPrice = (v, currency = "EUR") => fmtCurrency(toNumber(v), currency || "EUR");

  // UI state
  const [activeTab, setActiveTab] = useState("pending");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ===== All patients (admin view) =====
  const [allPatients, setAllPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientSearch, setPatientSearch] = useState("");

  // ===== Patient activity inspector (per patient) =====
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPatient, setActivityPatient] = useState(null);
  const [activityData, setActivityData] = useState({
    appointments: [],
    medicalRecords: [],
    documents: [],
    reviews: [],
  });
 const openPatientActivity = async (patient) => {
  if (!patient?.id) return;
  setActivityPatient(patient);
  setActivityOpen(true);
  setActivityLoading(true);

  try {
    const [apptsSnap, recordsSnap, docsSnap, reviewsSnap] = await Promise.all([
      // 1) Appointmentet e pacientit (10 më të fundit)
      getDocs(
        query(
          collection(db, "appointments"),
          where("patientId", "==", patient.id),
          orderBy("createdAt", "desc"),
          limit(10)
        )
      ),

      // 2) Medical records (p.sh. koleksion global "medicalRecords")
      getDocs(
        query(
          collection(db, "medicalRecords"),
          where("patientId", "==", patient.id),
          orderBy("createdAt", "desc"),
          limit(10)
        )
      ),

      // 3) Dokumentet e ngarkuara nga ky user (userDocuments)
      getDocs(
        query(
          collection(db, "userDocuments"),
          where("ownerId", "==", patient.id),
          orderBy("createdAt", "desc"),
          limit(10)
        )
      ),

      // 4) Reviews të shkruara nga ky pacient
      getDocs(
        query(
          collection(db, "reviews"),
          where("patientId", "==", patient.id),
          orderBy("createdAt", "desc"),
          limit(10)
        )
      ),
    ]);

    const mapSnap = (snap) =>
      snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    setActivityData({
      appointments: mapSnap(apptsSnap),
      medicalRecords: mapSnap(recordsSnap),
      documents: mapSnap(docsSnap),
      reviews: mapSnap(reviewsSnap),
    });
  } catch (e) {
    console.error("Failed to load patient activity", e);
    setActivityData({
      appointments: [],
      medicalRecords: [],
      documents: [],
      reviews: [],
    });
  } finally {
    setActivityLoading(false);
  }
};

  
    // ===== Homepage Promo Editor (patient portal) =====
  const [promoSlotId, setPromoSlotId] = useState("patient-home-main");
  const [promo, setPromo] = useState({
    topLabel: "",
    title: "",
    subtitle: "",
    ctaLabel: "",
    ctaPath: "",
    imageUrl: "",
    audience: "patient",
    audiences: [],
    active: true,
    validFrom: "",
    validTo: "",
  });
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoSaving, setPromoSaving] = useState(false);

  const PROMO_SLOTS = [
    { id: "patient-home-main", label: "Patient Home – Main Banner" },
    { id: "patient-home-secondary", label: "Patient Home – Secondary Banner" },
  ];

  const PROMO_AUDIENCES = ["all", "patient", "doctor", "clinic"];

  const handlePromoChange = (field, value) =>
    setPromo((p) => ({ ...p, [field]: value }));

  const handlePromoAudienceListChange = (value) => {
    const parts = value
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    setPromo((p) => ({ ...p, audiences: parts }));
  };

  const loadPromo = async (slotId) => {
    if (!isAdmin) return;
    setPromoLoading(true);
    try {
      const ref = doc(db, "promoSlots", slotId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setPromo({
          topLabel: data.topLabel || "",
          title: data.title || "",
          subtitle: data.subtitle || "",
          ctaLabel: data.ctaLabel || "",
          ctaPath: data.ctaPath || "",
          imageUrl: data.imageUrl || "",
          audience: data.audience || "all",
          audiences: data.audiences || [],
          active: data.active ?? true,
          validFrom: data.validFrom || "",
          validTo: data.validTo || "",
        });
      } else {
        // default content if no doc yet
        setPromo({
          topLabel: "",
          title: "",
          subtitle: "",
          ctaLabel: "",
          ctaPath: "",
          imageUrl: "",
          audience: "patient",
          audiences: [],
          active: true,
          validFrom: "",
          validTo: "",
        });
      }
    } catch (e) {
      console.error("Failed to load promo slot", slotId, e);
    } finally {
      setPromoLoading(false);
    }
  };

  const savePromo = async () => {
    if (!promo.title?.trim()) {
      alert("Titulli është i detyrueshëm.");
      return;
    }
    setPromoSaving(true);
    try {
      const ref = doc(db, "promoSlots", promoSlotId);
      await setDoc(
        ref,
        {
          slot: promoSlotId,
          ...promo,
        },
        { merge: true }
      );
      alert("Promo u ruajt ✅");
    } catch (e) {
      console.error("Failed to save promo", e);
      alert("Nuk u ruajt promo. Provo përsëri.");
    } finally {
      setPromoSaving(false);
    }
  };

  // load when admin or slot changes
  useEffect(() => {
    if (!isAdmin) return;
    loadPromo(promoSlotId);
  }, [isAdmin, promoSlotId]);

  // Load ALL patients for admin
  useEffect(() => {
    if (!isAdmin) return;

    const run = async () => {
      setPatientsLoading(true);
      try {
        const patientsQ = query(
          collection(db, "users"),
          where("role", "==", "patient")
          // you can add orderBy("createdAt", "desc") later if you add index
        );

        const snap = await getDocs(patientsQ);
        const list = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            name: data.name || data.profile?.name || "",
            email: data.email || "",
            phone: data.phone || data.profile?.phone || "",
            createdAt:
              data.createdAt?.toDate?.()?.toISOString?.().slice(0, 10) || "",
            isActive: data.isActive !== false, // default true
          };
        });

        setAllPatients(list);
      } catch (e) {
        console.error("Load all patients failed", e);
      } finally {
        setPatientsLoading(false);
      }
    };

    run();
  }, [isAdmin]);
  const filteredPatients = useMemo(() => {
    const q = patientSearch.trim().toLowerCase();
    if (!q) return allPatients;
    return allPatients.filter((p) => {
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.email || "").toLowerCase().includes(q) ||
        (p.phone || "").toLowerCase().includes(q)
      );
    });
  }, [allPatients, patientSearch]);

  // Analytics (incl services)
  const [stats, setStats] = useState({
    patientsCount: 0,
    totalAppointments: 0,
    activePatients30d: 0,
    storageBytes: 0,
    recentPatients: [],
    topPatients: [],
    topDoctors: [],
    apptStatus: { pending: 0, confirmed: 0, completed: 0, cancelled: 0 },
    growth: { current: 0, previous: 0, deltaPct: 0 },
    weeklyBuckets: [],
    revenue: {
      total: 0,
      total30d: 0,
      prev30d: 0,
      deltaPct30d: 0,
      avgPerCompleted: 0,
      byDoctor: [],
      topRevenueDoctors: []
    },
    services: {
      total: 0,
      catalog: [] // [{doctorId, doctorName, title, description, price}]
    }
  });

  // Seeded suggestions (add/adjust as you wish)
const KEYWORD_SEED = {
  "Radiologji": ["radiografi", "rreze X", "x-ray", "eko", "ultratinguj", "CT", "skaner", "MRI", "rezonancë"],
  "Kardiologji": ["kardiolog", "zemër", "ekg", "stres-test", "ekokardiogram", "arritmi", "presion gjaku"],
  "Dermatologji": ["dermatolog", "lëkurë", "akne", "psoriazë", "spote", "alergji lëkure", "ekzema"],
  "Gjinekologji & Obstetrikë": ["gjinekolog", "pap test", "eko shtatzënie", "fertilitet", "kontracepsion"],
  "Ortopedi": ["ortoped", "fraktura", "dhimbje kyçi", "MRI gju", "menisk", "ligament"],
  "Oftalmologji": ["oftalmolog", "syze", "lazer syri", "katarakt", "kontroll i syve"],
  "Endokrinologji": ["endokrinolog", "tiroide", "diabet", "insulinë", "hormone"],
  "Stomatologji": ["dentist", "stomatolog", "mbushje", "zbardhim", "implant", "kure kanali"],
};

// Quick token suggestions from a service title
const suggestFromTitle = (title = "") => {
  const base = (title || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s\-]/gu, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter(w => w.length >= 3);
  // de-dupe
  return [...new Set(base)];
};

const parseKeywords = (text = "") =>
  [...new Set(text.split(",").map(s => s.trim()).filter(Boolean))];

// Sponsored override: write paraRankscore (0..100) and flag sponsored
const handleSaveParaRank = async (uid, value) => {
  const score = Math.max(0, Math.min(100, Number(value) || 0));
  await updateDoc(doc(db, "doctors", uid), {
    paraRankscore: score,
    sponsored: true,
    rankingUpdatedAt: serverTimestamp(),
  });
  setDoctorsById(prev => ({ ...prev, [uid]: { ...prev[uid], paraRankscore: score, sponsored: true } }));
  alert(`Sponsored rank set to ${score}`);
};

const handleClearParaRank = async (uid) => {
  await updateDoc(doc(db, "doctors", uid), {
    paraRankscore: null,
    sponsored: false,
    rankingUpdatedAt: serverTimestamp(),
  });
  setDoctorsById(prev => ({ ...prev, [uid]: { ...prev[uid], paraRankscore: null, sponsored: false } }));
  alert("Sponsored rank cleared");
};

  const navigate = useNavigate();

  /* ===== Auth + admin role ===== */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (current) => {
      if (!current) { navigate("/login"); return; }
      setUser(current);
      try {
        const us = await getDoc(doc(db, "users", current.uid));
        if (us.exists() && us.data().role === "admin") {
          setIsAdmin(true);
        } else {
          navigate("/unauthorized");
        }
      } catch {
        navigate("/login");
      } finally {
        setCheckingRole(false);
      }
    });
    return () => unsub();
  }, [navigate]);

  /* ===== Load verification queues + doctors + reconciliation ===== */
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      // Load verifications
      const snapshot = await getDocs(collection(db, "verifications"));
      const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // Load doctors collection
      const docsSnap = await getDocs(collection(db, "doctors"));
      const docsMap = {};
      docsSnap.forEach(d => { docsMap[d.id] = { id: d.id, ...d.data() }; });
      setDoctorsById(docsMap);

      // Enrich verifications with profileData and linked doctor doc
      const enriched = await Promise.all(all.map(async (v) => {
        try {
          const docId = v.doctorId || v.uid || v.id;
          const dd = await getDoc(doc(db, "doctors", docId));
          return { ...v, profileData: dd.exists() ? dd.data() : {}, linkedDoctor: docsMap[docId] || null };
        } catch { return v; }
      }));

      setPending(enriched.filter(d => d.status === "pending"));
      setApproved(enriched.filter(d => d.status === "approved"));
      setRejected(enriched.filter(d => d.status === "rejected"));

      // Reconciliation sets
      const approvedIds = new Set(enriched.filter(x => x.status === "approved").map(x => x.doctorId || x.uid || x.id));
      const doctorIds = new Set(Object.keys(docsMap));
      const approvedNotInDoctors = [...approvedIds].filter(id => !doctorIds.has(id));
      const doctorsNotVerified = [...doctorIds].filter(id => !approvedIds.has(id));
      setRecon({ approvedNotInDoctors, doctorsNotVerified });
    })();
  }, [isAdmin]);

  /* ===== Analytics (appointments/patients/revenue/services/storage) ===== */
  useEffect(() => {
    if (!isAdmin) return;

    const APPT_FETCH_LIMIT = 5000; // adjust depth if needed
    const run = async () => {
      try {
        // Totals
        const patientsQ = query(collection(db, "users"), where("role", "==", "patient"));
        const patientsCntSnap = await getCountFromServer(patientsQ);
        const apptsCol = collection(db, "appointments");
        const apptsCntSnap = await getCountFromServer(apptsCol);

        // Status breakdown
        const countStatus = async (s) => {
          try {
            const qy = query(collection(db, "appointments"), where("status", "==", s));
            const c = await getCountFromServer(qy);
            return c.data().count || 0;
          } catch { return 0; }
        };
        const [pendingC, confirmedC, completedC, cancelledC] = await Promise.all([
          countStatus("pending"), countStatus("confirmed"), countStatus("completed"), countStatus("cancelled")
        ]);

        // Pull recent appointments for analytics & revenue
        const now = new Date();
        const d30 = new Date(now); d30.setDate(d30.getDate() - 30);
        const d60 = new Date(now); d60.setDate(d60.getDate() - 60);
        const d56 = new Date(now); d56.setDate(d56.getDate() - 56); // 8 weeks

        const recentApptsQ = query(collection(db, "appointments"), orderBy("createdAt", "desc"), limit(APPT_FETCH_LIMIT));
        const recentSnap = await getDocs(recentApptsQ);
        const appts = recentSnap.docs.map(d => ({
          id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate?.() || new Date(0)
        }));

        // Active patients (30d)
        const activeSet = new Set(appts.filter(a => a.createdAt >= d30).map(a => a.patientId).filter(Boolean));

        // Patient growth (30d vs prev 30d)
        const currentPatients = new Set(appts.filter(a => a.createdAt >= d30).map(a => a.patientId).filter(Boolean));
        const previousPatients = new Set(appts.filter(a => a.createdAt >= d60 && a.createdAt < d30).map(a => a.patientId).filter(Boolean));
        const growthDeltaPct = (() => {
          const prev = previousPatients.size || 1;
          return ((currentPatients.size - prev) / prev) * 100;
        })();

        // Weekly buckets (8 weeks)
        const toWeekKey = (date) => {
          const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
          const dayNum = d.getUTCDay() || 7;
          d.setUTCDate(d.getUTCDate() + 4 - dayNum);
          const yearStart = new Date(Date.UTC(d.getUTCFullYear(),0,1));
          const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
          return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2,"0")}`;
        };
        const bucketsMap = new Map();
        appts.filter(a => a.createdAt >= d56).forEach(a => {
          const key = toWeekKey(a.createdAt);
          bucketsMap.set(key, (bucketsMap.get(key) || 0) + 1);
        });
        const keys = [];
        let cursor = new Date(d56);
        for (let i=0;i<8;i++){ keys.push(toWeekKey(cursor)); cursor.setDate(cursor.getDate()+7); }
        const uniqKeys = [...new Set(keys)];
        const weeklyBuckets = uniqKeys.map(k => ({ label: k, value: bucketsMap.get(k) || 0 }));

        // Storage usage (uploads -> items subcollections)
        let storageBytes = 0;
        try {
          const uploadsCG = query(collectionGroup(db, "items"), limit(2000));
          const uploadsSnap = await getDocs(uploadsCG);
          uploadsSnap.forEach(u => {
            const sz = Number(u.data().sizeBytes || 0);
            storageBytes += isNaN(sz) ? 0 : sz;
          });
        } catch {}

        // Recent patients (fallback if createdAt not indexed)
        let recentPatients = [];
        try {
          const recentPatientsQ = query(
            collection(db, "users"),
            where("role", "==", "patient"),
            orderBy("createdAt", "desc"),
            limit(10)
          );
          const rpSnap = await getDocs(recentPatientsQ);
          recentPatients = rpSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch {
          const rpSnap = await getDocs(patientsQ);
          recentPatients = rpSnap.docs.slice(0, 10).map(d => ({ id: d.id, ...d.data() }));
        }

        // Top patients by visit count across fetched appts
        const patientCounts = new Map();
        appts.forEach(a => {
          if (!a.patientId) return;
          const meta = patientCounts.get(a.patientId) || { count: 0, name: a.patientInfo?.name, email: a.patientInfo?.email };
          meta.count += 1;
          if (!meta.name && a.patientInfo?.name) meta.name = a.patientInfo.name;
          if (!meta.email && a.patientInfo?.email) meta.email = a.patientInfo.email;
          patientCounts.set(a.patientId, meta);
        });
        const topPatients = [...patientCounts.entries()]
          .sort((a,b) => b[1].count - a[1].count)
          .slice(0,5).map(([patientId, meta]) => ({ patientId, ...meta }));

        // Revenue totals
        const completed = appts.filter(a => a.status === "completed");
        const totalRevenue = completed.reduce((sum, a) => sum + getPrice(a), 0);
        const avgPerCompleted = completed.length ? totalRevenue / completed.length : 0;

        // Revenue 30d vs prev30d
        const completed30d = completed.filter(a => a.createdAt >= d30);
        const rev30d = completed30d.reduce((sum, a) => sum + getPrice(a), 0);
        const prev30dCompleted = completed.filter(a => a.createdAt >= d60 && a.createdAt < d30);
        const prev30d = prev30dCompleted.reduce((sum, a) => sum + getPrice(a), 0);
        const deltaPct30d = ((rev30d - (prev30d || 1)) / (prev30d || 1)) * 100;

        // Revenue by doctor
        const revenueByDoctorMap = new Map();
        completed.forEach(a => {
          if (!a.doctorId) return;
          const key = a.doctorId;
          const cur = revenueByDoctorMap.get(key) || { revenue: 0, completedCount: 0, name: a.doctorInfo?.name };
          cur.revenue += getPrice(a);
          cur.completedCount += 1;
          if (!cur.name && a.doctorInfo?.name) cur.name = a.doctorInfo.name;
          revenueByDoctorMap.set(key, cur);
        });
        const byDoctor = [...revenueByDoctorMap.entries()]
          .map(([doctorId, meta]) => ({ doctorId, ...meta }))
          .sort((a,b) => b.revenue - a.revenue);
        const topRevenueDoctors = byDoctor.slice(0, 5);

        // Top doctors by completed count (30d)
        const doctorCounts = new Map();
        completed.filter(a => a.createdAt >= d30).forEach(a => {
          const key = a.doctorId;
          if (!key) return;
          const cur = doctorCounts.get(key) || { count: 0, name: a.doctorInfo?.name };
          cur.count += 1; if (!cur.name && a.doctorInfo?.name) cur.name = a.doctorInfo.name;
          doctorCounts.set(key, cur);
        });
        const topDoctors = [...doctorCounts.entries()]
          .sort((a,b) => b[1].count - a[1].count)
          .slice(0,5).map(([doctorId, info]) => ({ doctorId, ...info }));

        // ===== Services (from doctors collection) =====
        const doctorsSnap = await getDocs(collection(db, "doctors"));
        const doctors = doctorsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const doctorNameById = {};
        doctors.forEach((d) => { doctorNameById[d.id] = d.name || d.profile?.name || "Unknown Doctor"; });
        let totalServices = 0;
        const catalog = [];
        doctors.forEach((d) => {
          const arr = Array.isArray(d.services) ? d.services : [];
          totalServices += arr.length;
          arr.forEach((s) => {
            catalog.push({
              doctorId: d.id,
              doctorName: doctorNameById[d.id],
              title: s.title || "Untitled",
              description: s.description || "",
              price: s?.price ?? null,
              currency: s?.currency || d?.currency || "EUR",
            });
          });
        });

        // Commit
        setStats({
          patientsCount: patientsCntSnap.data().count || 0,
          totalAppointments: apptsCntSnap.data().count || 0,
          activePatients30d: activeSet.size,
          storageBytes,
          recentPatients,
          topPatients,
          topDoctors,
          apptStatus: { pending: pendingC, confirmed: confirmedC, completed: completedC, cancelled: cancelledC },
          growth: { current: currentPatients.size, previous: previousPatients.size, deltaPct: growthDeltaPct },
          weeklyBuckets,
          revenue: {
            total: totalRevenue,
            total30d: rev30d,
            prev30d,
            deltaPct30d,
            avgPerCompleted,
            byDoctor,
            topRevenueDoctors
          },
          services: {
            total: totalServices,
            catalog
          }
        });
      } catch (e) {
        console.error("Admin analytics fetch error:", e);
      }
    };

    run();
  }, [isAdmin]);

  /* ===== Ranking: compute score & update ===== */
  // Weights (from your algorithm): rating 30, completed 10, posts 5, years 10,
  // profile completeness 10, total appts 20, verified 10, followers 5
  const weights = {
    rating: 0.30,
    completed: 0.10,
    posts: 0.05,
    years: 0.10,
    completeness: 0.10,
    totalAppts: 0.20,
    verified: 0.10,
    followers: 0.05
  };
/* ===== Ranking helpers for "why" UI ===== */
const getCohortMax = (map) => {
  const list = Object.values(map || {});
  const safeMax = (arr, floor=10) => Math.max(...arr, floor);
  return {
    completedMax: safeMax(list.map(d => Number(d.completedCount ?? d.metrics?.completedCount ?? 0))),
    postsMax:     safeMax(list.map(d => Number(d.postsCount     ?? d.metrics?.posts     ?? 0))),
    yearsMax:     safeMax(list.map(d => Number(d.yearsOfExperience ?? d.experienceYears ?? 0))),
    totalApptsMax:safeMax(list.map(d => Number(d.appointmentsCount ?? d.metrics?.appointments ?? 0))),
    followersMax: safeMax(list.map(d => Number(d.followers ?? d.metrics?.followers ?? 0))),
  };
};

// Returns detailed rows for the UI: label, raw, normalized(0..1), weight, points(0..100)
const explainRanking = (d, cohortMax) => {
  const rating5   = Number(d.ratingAvg ?? d.rating ?? 0);
  const completed = Number(d.completedCount ?? d.metrics?.completedCount ?? 0);
  const posts     = Number(d.postsCount ?? d.metrics?.posts ?? 0);
  const years     = Number(d.yearsOfExperience ?? d.experienceYears ?? 0);
  const totalAppt = Number(d.appointmentsCount ?? d.metrics?.appointments ?? 0);
  const followers = Number(d.followers ?? d.metrics?.followers ?? 0);
  const isVer     = Boolean(d.verified ?? d.isVerified ?? d.badges?.includes?.("verified"));

  const rows = [
    { key:"rating",        label:"Rating (0–5)",                raw: rating5,          norm: normalize(rating5, 5),                           w: weights.rating },
    { key:"completed",     label:"Completed visits",            raw: completed,         norm: normalize(completed, cohortMax.completedMax),   w: weights.completed },
    { key:"posts",         label:"Posts",                       raw: posts,             norm: normalize(posts, cohortMax.postsMax),           w: weights.posts },
    { key:"years",         label:"Years of experience",         raw: years,             norm: normalize(years, cohortMax.yearsMax),           w: weights.years },
    { key:"completeness",  label:"Profile completeness",        raw: Math.round(profileCompleteness(d)*100)+"%", norm: profileCompleteness(d), w: weights.completeness },
    { key:"totalAppts",    label:"Total appointments",          raw: totalAppt,         norm: normalize(totalAppt, cohortMax.totalApptsMax),  w: weights.totalAppts },
    { key:"verified",      label:"Verified badge",              raw: isVer ? "Yes" : "No", norm: isVer ? 1 : 0,                               w: weights.verified },
    { key:"followers",     label:"Followers",                   raw: followers,         norm: normalize(followers, cohortMax.followersMax),   w: weights.followers },
  ];
  return rows.map(r => ({ ...r, points: Math.round(r.norm * r.w * 100) }));
};

// Tiny inline bar for each factor
const Bar = ({ value=0 }) => (
  <div style={{height:8, background:"#f1f5f9", borderRadius:999, overflow:"hidden"}}>
    <div style={{width:`${Math.min(100, Math.max(0, value))}%`, height:"100%", background:"#3b82f6"}} />
  </div>
);

  const profileCompleteness = (d) => {
    const fields = [
      d.name || d.profile?.name,
      d.specialty || d.profile?.specialty,
      d.bio || d.profile?.bio,
      d.location?.address || d.profile?.location?.address,
      (Array.isArray(d.services) && d.services.length > 0) ? "services" : "",
      d.photoURL || d.profilePicture,
    ];
    const filled = fields.filter(Boolean).length;
    return filled / fields.length; // 0..1
  };

  const normalize = (value, max) => {
    if (!max || max <= 0) return 0;
    const v = Math.max(0, Number(value || 0));
    return Math.min(v / max, 1);
  };

  const computeRanking = (d, cohortMax = null) => {
    // Derive counters with safe fallbacks
    const rating5 = Number(d.ratingAvg ?? d.rating ?? 0);          // 0..5
    const completed = Number(d.completedCount ?? d.metrics?.completedCount ?? 0);
    const posts = Number(d.postsCount ?? d.metrics?.posts ?? 0);
    const years = Number(d.yearsOfExperience ?? d.experienceYears ?? 0);
    const totalAppts = Number(d.appointmentsCount ?? d.metrics?.appointments ?? 0);
    const followers = Number(d.followers ?? d.metrics?.followers ?? 0);
    const isVerified = Boolean(d.verified ?? d.isVerified ?? d.badges?.includes?.("verified"));

    // Cohort maxes for normalization (if provided from bulk run)
    const maxes = cohortMax || {
      completedMax: Math.max(10, completed),
      postsMax: Math.max(10, posts),
      yearsMax: Math.max(10, years),
      totalApptsMax: Math.max(10, totalAppts),
      followersMax: Math.max(10, followers),
    };

    const score =
      weights.rating       * normalize(rating5, 5) +
      weights.completed    * normalize(completed, maxes.completedMax) +
      weights.posts        * normalize(posts, maxes.postsMax) +
      weights.years        * normalize(years, maxes.yearsMax) +
      weights.completeness * profileCompleteness(d) +
      weights.totalAppts   * normalize(totalAppts, maxes.totalApptsMax) +
      weights.verified     * (isVerified ? 1 : 0) +
      weights.followers    * normalize(followers, maxes.followersMax);

    // Return 0..100
    return Math.round(score * 100);
  };
useEffect(() => {
  // Build a flat list of all services across doctors
  const rows = [];
  Object.values(doctorsById || {}).forEach(d => {
    const doctorId   = d.id;
    const doctorName = d.name || d.profile?.name || "Doctor";
    const specialty  = d.specialty || d.profile?.specialty || "";
    const services   = Array.isArray(d.services) ? d.services : [];

    services.forEach((s, idx) => {
      const key = `${doctorId}#${idx}`;
      rows.push({
        key,
        doctorId,
        doctorName,
        specialty,
        serviceIndex: idx,
        title: s?.title || "Untitled",
        price: s?.price ?? null,
        currency: s?.currency || d?.currency || "EUR",
        keywords: Array.isArray(s?.keywords) ? s.keywords : [],
      });
    });
  });
  setKwRows(rows);
  setKwDirty(new Set());
}, [doctorsById]);

  const handleRecalcAndSaveRanking = async (uid) => {
    const d = doctorsById[uid];
    if (!d) return alert("Doctor document not found.");
    const score = computeRanking(d);
    await updateDoc(doc(db, "doctors", uid), {
      rankingScore: score,
      rankingUpdatedAt: serverTimestamp()
    });
    setDoctorsById(prev => ({ ...prev, [uid]: { ...prev[uid], rankingScore: score } }));
    alert(`Ranking updated to ${score}`);
  };
const saveRowKeywords = async (row) => {
  const d = doctorsById[row.doctorId];
  if (!d) return alert("Doctor not found.");
  const services = Array.isArray(d.services) ? [...d.services] : [];
  const svc = services[row.serviceIndex] || {};
  services[row.serviceIndex] = { ...svc, keywords: row.keywords };

  await updateDoc(doc(db, "doctors", row.doctorId), {
    services,
    updatedAt: serverTimestamp(),
  });

  // reflect local
  setDoctorsById(prev => ({ ...prev, [row.doctorId]: { ...prev[row.doctorId], services } }));
  setKwDirty(prev => {
    const next = new Set(prev);
    next.delete(row.key);
    return next;
  });
};

const saveAllDirtyKeywords = async () => {
  const dirtyKeys = [...kwDirty];
  if (!dirtyKeys.length) return;

  // group by doctorId to reduce writes
  const byDoc = new Map();
  dirtyKeys.forEach(k => {
    const r = kwRows.find(x => x.key === k);
    if (!r) return;
    if (!byDoc.has(r.doctorId)) byDoc.set(r.doctorId, []);
    byDoc.get(r.doctorId).push(r);
  });

  for (const [doctorId, rows] of byDoc.entries()) {
    const d = doctorsById[doctorId];
    if (!d) continue;
    const services = Array.isArray(d.services) ? [...d.services] : [];
    rows.forEach(r => {
      const i = r.serviceIndex;
      const svc = services[i] || {};
      services[i] = { ...svc, keywords: r.keywords };
    });
    try {
      await updateDoc(doc(db, "doctors", doctorId), {
        services,
        updatedAt: serverTimestamp(),
      });
      setDoctorsById(prev => ({ ...prev, [doctorId]: { ...prev[doctorId], services } }));
    } catch (e) {
      console.error("Save keywords failed", doctorId, e);
    }
  }

  setKwDirty(new Set());
  alert("Saved keywords for edited services.");
};
const exportServiceKeywords = () => {
  if (!kwRows.length) return;
  const rows = kwRows.map(r => ({
    doctorId: r.doctorId,
    doctorName: r.doctorName,
    specialty: r.specialty,
    serviceIndex: r.serviceIndex,
    title: r.title,
    price: r.price ?? "",
    keywords: (r.keywords || []).join(", "),
  }));
  downloadCSV(rows, "service_keywords.csv");
};

// CSV columns expected: doctorId,serviceIndex,keywords
const importServiceKeywords = async (file) => {
  const text = await file.text();
  const lines = text.split(/\r?\n/);
  const header = lines.shift();
  if (!header) return alert("Empty CSV.");
  const head = header.split(",");
  const iDoctor = head.findIndex(h => /doctorId/i.test(h));
  const iIndex  = head.findIndex(h => /serviceIndex/i.test(h));
  const iKw     = head.findIndex(h => /^keywords$/i.test(h));
  if (iDoctor < 0 || iIndex < 0 || iKw < 0) {
    return alert("CSV must have columns: doctorId, serviceIndex, keywords");
  }

  // Build a map doctorId -> updates [{idx, keywords[]}]
  const byDoc = new Map();
  for (const line of lines) {
    if (!line.trim()) continue;
    const cols = line.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/); // CSV-safe split
    const doctorId = (cols[iDoctor] || "").trim();
    const idx = Number((cols[iIndex] || "").trim());
    const kws = parseKeywords(cols[iKw] || "");
    if (!doctorId || Number.isNaN(idx)) continue;
    if (!byDoc.has(doctorId)) byDoc.set(doctorId, []);
    byDoc.get(doctorId).push({ idx, kws });
  }

  for (const [doctorId, updates] of byDoc.entries()) {
    const d = doctorsById[doctorId];
    if (!d) continue;
    const services = Array.isArray(d.services) ? [...d.services] : [];
    updates.forEach(u => {
      const svc = services[u.idx] || {};
      services[u.idx] = { ...svc, keywords: u.kws };
    });
    try {
      await updateDoc(doc(db, "doctors", doctorId), {
        services,
        updatedAt: serverTimestamp(),
      });
      setDoctorsById(prev => ({ ...prev, [doctorId]: { ...prev[doctorId], services } }));
    } catch (e) {
      console.error("Import keywords failed for", doctorId, e);
    }
  }
  alert("Imported keywords from CSV.");
};

  const recomputeAllRankings = async () => {
    const list = Object.values(doctorsById);
    if (!list.length) return;
    // Build cohort maxes
    const completedMax = Math.max(...list.map(d => Number(d.completedCount ?? d.metrics?.completedCount ?? 0)), 10);
    const postsMax = Math.max(...list.map(d => Number(d.postsCount ?? d.metrics?.posts ?? 0)), 10);
    const yearsMax = Math.max(...list.map(d => Number(d.yearsOfExperience ?? d.experienceYears ?? 0)), 10);
    const totalApptsMax = Math.max(...list.map(d => Number(d.appointmentsCount ?? d.metrics?.appointments ?? 0)), 10);
    const followersMax = Math.max(...list.map(d => Number(d.followers ?? d.metrics?.followers ?? 0)), 10);
    const cohortMax = { completedMax, postsMax, yearsMax, totalApptsMax, followersMax };

    // Update in small batches to avoid overloading
    const entries = Object.entries(doctorsById);
    for (let i = 0; i < entries.length; i++) {
      const [uid, d] = entries[i];
      const score = computeRanking(d, cohortMax);
      try {
        await updateDoc(doc(db, "doctors", uid), {
          rankingScore: score,
          rankingUpdatedAt: serverTimestamp()
        });
        setDoctorsById(prev => ({ ...prev, [uid]: { ...prev[uid], rankingScore: score } }));
      } catch (e) {
        console.error("Ranking update failed for", uid, e);
      }
    }
    alert("Recomputed rankings for all doctors.");
  };

  /* ===== Actions (verify / delete / logout) ===== */
const handleUpdateStatus = async (verificationId, newStatus) => {
  await updateDoc(doc(db, "verifications", verificationId), { status: newStatus });

  const all = [...pending, ...approved, ...rejected];
  const item = all.find(v => v.id === verificationId);
  if (!item) return;
  const uid = item?.profileData?.uid || item?.doctorId || item?.uid || item?.id;

  if (newStatus === "approved" && uid) {
    try {
      await updateDoc(doc(db, "doctors", uid), {
        isVerified: true,
         isPublic: true,
         publishedAt: serverTimestamp(),
        rankingUpdatedAt: serverTimestamp(),
      });
      setDoctorsById(prev => prev?.[uid] ? ({ ...prev, [uid]: { ...prev[uid], isVerified: true,publishedAt: serverTimestamp(), } }) : prev);
    } catch (e) { console.error("Failed to set isVerified:", e); }
  }

  setPending(prev => prev.filter(v => v.id !== verificationId));
  setApproved(prev => prev.filter(v => v.id !== verificationId));
  setRejected(prev => prev.filter(v => v.id !== verificationId));
  const updated = { ...item, status: newStatus };
  if (newStatus === "approved") setApproved(prev => [...prev, updated]);
  if (newStatus === "rejected") setRejected(prev => [...prev, updated]);
};



  const handleDeleteProfile = async (verificationId, doctorUid) => {
    try {
      await deleteDoc(doc(db, "verifications", verificationId));
      if (doctorUid) {
        await deleteDoc(doc(db, "doctors", doctorUid));
        try { await deleteDoc(doc(db, "users", doctorUid)); } catch {}
      }
      setPending(prev => prev.filter(v => v.id !== verificationId));
      setApproved(prev => prev.filter(v => v.id !== verificationId));
      setRejected(prev => prev.filter(v => v.id !== verificationId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting profile:", error);
      alert("Failed to delete profile. Please try again.");
    }
  };
// ==== Service Mapping Modal ====
const [activeDoctor, setActiveDoctor] = useState(null);
const [activeServices, setActiveServices] = useState([]);
const [serviceModalOpen, setServiceModalOpen] = useState(false);

async function openDoctorServices(doctor) {
  setActiveDoctor(doctor);
  try {
    const docRef = doc(db, "services", doctor.id);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data().items || [];
      setActiveServices(data);
    } else {
      setActiveServices([]);
    }
    setServiceModalOpen(true);
  } catch (e) {
    console.error("Error loading doctor services", e);
    alert("Failed to load services for this doctor");
  }
}

async function saveServiceKeywords(doctorId, index, keywords) {
  try {
    const docRef = doc(db, "services", doctorId);
    await updateDoc(docRef, {
      [`items.${index}.keywords`]: keywords,
    });
    setActiveServices(prev => {
      const next = [...prev];
      next[index].keywords = keywords;
      return next;
    });
  } catch (e) {
    console.error("Failed to save keywords", e);
    alert("Could not save keywords");
  }
}

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch {
      alert("Failed to logout. Please try again.");
    }
  };

  /* ===== Search/filter in tab ===== */
  const currentList = activeTab === "pending" ? pending : activeTab === "approved" ? approved : rejected;
  const filteredList = useMemo(() => {
    const q = safeLower(searchText);
    if (!q) return currentList;
    return currentList.filter(item => {
      const p = item.profileData || {};
      return [p.name, p.specialty, item.doctorEmail, p.location?.address].some(x => safeLower(x).includes(q));
    });
  }, [currentList, searchText, activeTab]);

  /* ===== Bulk ops (pending) ===== */
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
const approveAllPending = async () => {
  if (!pending.length) return;

  await Promise.all(pending.map(p => updateDoc(doc(db, "verifications", p.id), { status: "approved" })));

  await Promise.all(
    pending.map(async (p) => {
      const uid = p?.profileData?.uid || p?.doctorId || p?.uid || p?.id;
      if (!uid) return;
      try {
        await updateDoc(doc(db, "doctors", uid), {
          isVerified: true,
          isPublic: true,
          publishedAt: serverTimestamp(),
          rankingUpdatedAt: serverTimestamp(),
        });
        setDoctorsById(prev => prev?.[uid] ? ({ ...prev, [uid]: { ...prev[uid], isVerified: true ,isPublic: true, publishedAt: new Date()} }) : prev);
      } catch (e) { console.error("Failed isVerified backfill for", uid, e); }
    })
  );

  setApproved(prev => [...prev, ...pending.map(p => ({ ...p, status: "approved" }))]);
  setPending([]);
  clearSelection();
};


const backfillApprovedVerified = async () => {
  if (!approved.length) return;
  await Promise.all(
    approved.map(async (v) => {
      const uid = v?.profileData?.uid || v?.doctorId || v?.uid || v?.id;
      if (!uid) return;
      try {
        await updateDoc(doc(db, "doctors", uid), {
          isVerified: true,
          rankingUpdatedAt: serverTimestamp(),
             isPublic: true,
   publishedAt: serverTimestamp(),
        });
        setDoctorsById(prev => prev?.[uid] ? ({ ...prev, [uid]: { ...prev[uid], isVerified: true } }) : prev);
      } catch (e) { console.error("Backfill isVerified failed for", uid, e); }
    })
  );
  alert("Backfill complete: all APPROVED doctors flagged as isVerified = true.");
};


  const rejectSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    await Promise.all(ids.map(id => updateDoc(doc(db, "verifications", id), { status: "rejected" })));
    const moved = pending.filter(p => selectedIds.has(p.id)).map(p => ({ ...p, status: "rejected" }));
    setPending(prev => prev.filter(p => !selectedIds.has(p.id)));
    setRejected(prev => [...prev, ...moved]);
    clearSelection();
  };

  /* ====================== UI bits ====================== */
  const Row = ({ icon, text }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#374151", fontSize: 14 }}>
      {icon}
      <span>{text}</span>
    </div>
  );

  const Card = ({ doctor, showActions = false }) => {
    const isExpanded = expandedCard === doctor.id;
    const p = doctor.profileData || {};
    const isChecked = selectedIds.has(doctor.id);
    const uid = p.uid || doctor.doctorId || doctor.id;
    const linked = doctorsById[uid];

    return (
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16,
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.08)", marginBottom: 16, overflow: "hidden"
      }}>
        {/* Header */}
        <div style={{ padding: "16px 18px", borderBottom: "1px solid #f3f4f6", display: "grid", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", gap: 12 }}>
              {activeTab === "pending" && (
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleSelect(doctor.id)}
                  style={{ marginTop: 6 }}
                  aria-label="Select doctor"
                />
              )}
              <div style={{
                width: 42, height: 42, borderRadius: 12,
                background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700
              }}>
                {(p.name || "D").slice(0,1).toUpperCase()}
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>
                    {p.name || linked?.name || "Doctor Name"}
                  </h3>
                  <span style={{
                    padding: "4px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                    backgroundColor: doctor.status === "approved" ? "#dcfce7" :
                                    doctor.status === "rejected" ? "#fee2e2" : "#fef3c7",
                    color: doctor.status === "approved" ? "#166534" :
                           doctor.status === "rejected" ? "#991b1b" : "#92400e"
                  }}>
                    {doctor.status.toUpperCase()}
                  </span>
                  {linked && (
  <>
    <span style={{
      padding:"4px 8px", borderRadius:999, fontSize:11, fontWeight:800,
      background:"#eef2ff", color:"#3730a3"
    }}>
      Rank (effective): {effectiveScore(linked).toFixed(2)}
    </span>

    {typeof linked.rankScore === "number" && (
      <span style={{
        padding:"4px 8px", borderRadius:999, fontSize:11, fontWeight:800,
        background:"#f3f4f6", color:"#111827"
      }}>
        Server: {Number(linked.rankScore).toFixed(2)}
      </span>
    )}

    {typeof linked.paraRankscore === "number" && (
      <span style={{
        padding:"4px 8px", borderRadius:999, fontSize:11, fontWeight:800,
        background:"#fff7ed", color:"#9a3412"
      }}>
        Sponsored
      </span>
    )}
  </>
)}

                </div>
                
                <div style={{ color: "#6b7280", fontSize: 12 }}>{doctor.doctorEmail || "—"}</div>
                {(p.specialty || linked?.specialty) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, color: "#374151", fontSize: 13 }}>
                    <Stethoscope size={14} /> {p.specialty || linked?.specialty}
                  </div>
                )}
                {(p.location || linked?.location) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, color: "#374151", fontSize: 13 }}>
                    <MapPin size={14} /> {(p.location?.address || p.location) ?? (linked?.location?.address || linked?.location)}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {uid && (
                <a
                  href={`https://mikdok.netlify.app/doctor/${uid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: "8px 10px", borderRadius: 8, background: "#3b82f6", color: "#fff",
                    display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, textDecoration: "none"
                  }}
                >
                  <ExternalLink size={14} /> Profile
                </a>
              )}
{activeTab === "approved" && (
  <button
    onClick={backfillApprovedVerified}
    disabled={!approved.length}
    style={{ padding:"10px 12px", border:"1px solid #1d4ed8", background:"#1d4ed8", color:"#fff",
             borderRadius:10, fontWeight:800, cursor: approved.length ? "pointer" : "not-allowed" }}
    title="Set isVerified=true on all approved doctors"
  >
    Backfill Verified (Approved)
  </button>
)}


              <button
                onClick={() => setExpandedCard(isExpanded ? null : doctor.id)}
                style={{
                  padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db",
                  background: "#fff", color: "#374151", fontSize: 12, fontWeight: 600, cursor: "pointer"
                }}
              >
                {isExpanded ? "Hide" : "Details"}
              </button>
            </div>
          </div>
        </div>

        {/* Details */}
        <div style={{ padding: 16 }}>
          {/* Quick info grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            {(p.phone || linked?.phone) && (
              <Row icon={<Phone size={14} />} text={p.phone ?? linked?.phone} />
            )}
            {(p.startedPracticeYear || linked?.startedPracticeYear) && (
              <Row icon={<Calendar size={14} />} text={`Since ${p.startedPracticeYear ?? linked?.startedPracticeYear}`} />
            )}
            {(p.experience || linked?.experience) && (
              <Row icon={<Award size={14} />} text={p.experience ?? linked?.experience} />
            )}
          </div>

          {/* Bio */}
          {(p.bio || linked?.bio) && (
            <div style={{
              marginTop: 10, background: "#f8fafc", border: "1px solid #eef2f7", padding: 10, borderRadius: 8,
              color: "#374151", fontSize: 13, lineHeight: 1.45
            }}>
              {p.bio ?? linked?.bio}
            </div>
          )}

          {/* Verification docs */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: "#111827", marginBottom: 6 }}>Verification Documents</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["idURL", "licenseURL", "diplomaURL"].map((k) =>
                doctor[k] ? (
                  <a key={k} href={doctor[k]} target="_blank" rel="noopener noreferrer"
                    style={{
                      padding: "6px 10px", borderRadius: 6, background: "#e5e7eb",
                      color: "#374151", fontSize: 12, fontWeight: 600, textDecoration: "none",
                      display: "inline-flex", alignItems: "center", gap: 6
                    }}>
                    <FileText size={14} /> {k.replace("URL","").toUpperCase()}
                  </a>
                ) : null
              )}
            </div>
          </div>
          {/* Ranking breakdown */}
{linked && (
  <div style={{ marginTop: 12 }}>
    <div style={{ fontWeight: 800, fontSize: 13, color: "#111827", marginBottom: 8 }}>
      Why this rank?
    </div>
    {(() => {
      const cohort = getCohortMax(doctorsById);
      const rows   = explainRanking(linked, cohort);
      const score  = typeof linked.rankingScore === "number" ? linked.rankingScore : computeRanking(linked, cohort);
      return (
        <div style={{ border:"1px solid #e5e7eb", borderRadius:10, padding:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10, flexWrap:"wrap" }}>
            <span style={{ fontSize:12, fontWeight:800, color:"#6b7280" }}>Overall score</span>
            <span style={{ fontSize:22, fontWeight:900, color:"#111827" }}>{score}</span>
            <button
              onClick={() => handleRecalcAndSaveRanking(uid)}
              style={{ marginLeft:"auto", padding:"6px 10px", border:"1px solid #d1d5db", background:"#fff", borderRadius:8, fontWeight:700, fontSize:12, display:"inline-flex", alignItems:"center", gap:6, cursor:"pointer" }}
              title="Recalculate and save ranking"
            >
              <RefreshCcw size={14} /> Recalc Rank
            </button>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1.1fr 80px 1fr 70px", gap:8, fontSize:12, fontWeight:800, color:"#6b7280", marginBottom:6 }}>
            <div>Factor</div><div style={{textAlign:"right"}}>Weight</div><div>Contribution</div><div style={{textAlign:"right"}}>Pts</div>
          </div>
          {rows.map(r => (
            <div key={r.key} style={{ display:"grid", gridTemplateColumns:"1.1fr 80px 1fr 70px", gap:8, alignItems:"center", padding:"6px 0", borderTop:"1px solid #f3f4f6" }}>
              <div>
                <div style={{ fontWeight:800, color:"#111827" }}>{r.label}</div>
                <div style={{ fontSize:12, color:"#6b7280" }}>Value: {r.raw} • Normalized: {(r.norm*100).toFixed(0)}%</div>
              </div>
              <div style={{ textAlign:"right", color:"#6b7280", fontWeight:800 }}>{Math.round(r.w*100)}%</div>
              <div><Bar value={r.points} /></div>
              <div style={{ textAlign:"right", fontWeight:900, color:"#111827" }}>{r.points}</div>
            </div>
          ))}
          <div style={{ marginTop:8, fontSize:11, color:"#6b7280" }}>
            Points = (normalized factor × weight) × 100. Total score is the sum of all factor points.
          </div>
        </div>
      );
    })()}
  </div>
)}
{/* Sponsored override + explainer */}
{linked && (() => {
  const uid = p.uid || doctor.doctorId || doctor.id;
  const exp = explainServerRank(linked);
  return (
    <div style={{ marginTop: 12, border:"1px solid #e5e7eb", borderRadius:12, padding:12, background:"#fbfdff" }}>
      {/* Sponsored editor */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr auto auto", gap:8, alignItems:"center",
                    border:"1px dashed #e5e7eb", padding:10, borderRadius:10, background:"#fafafa", marginBottom:12 }}>
        <div style={{ fontSize:12, color:"#6b7280" }}>
          <strong style={{ color:"#111827" }}>Sponsored override</strong> (paraRankscore) — shown to users if set.
        </div>
        <input
          type="number"
          min={0}
          max={100}
          defaultValue={typeof linked.paraRankscore === "number"
            ? linked.paraRankscore
            : (typeof linked.rankScore === "number" ? Number(linked.rankScore).toFixed(2) : exp.score.toFixed(2))}
          id={`para-input-${uid}`}
          style={{ width:110, padding:"6px 8px", border:"1px solid #d1d5db", borderRadius:8, fontWeight:700 }}
        />
        <div style={{ display:"flex", gap:8 }}>
          <button
            onClick={() => handleSaveParaRank(uid, document.getElementById(`para-input-${uid}`).value)}
            style={{ padding:"6px 10px", border:"1px solid #3b82f6", background:"#3b82f6", color:"#fff",
                     borderRadius:8, fontWeight:800, fontSize:12, cursor:"pointer" }}
          >
            Save
          </button>
          {typeof linked.paraRankscore === "number" && (
            <button
              onClick={() => handleClearParaRank(uid)}
              style={{ padding:"6px 10px", border:"1px solid #ef4444", background:"#ef4444", color:"#fff",
                       borderRadius:8, fontWeight:800, fontSize:12, cursor:"pointer" }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Why this rank? */}
      <div style={{ display:"flex", alignItems:"baseline", gap:12, flexWrap:"wrap", marginBottom:8 }}>
        <div style={{ fontSize:12, color:"#6b7280", fontWeight:800 }}>Server (rankScore)</div>
        <div style={{ fontSize:18, fontWeight:900, color:"#111827" }}>
          {typeof linked.rankScore === "number" ? Number(linked.rankScore).toFixed(2) : "—"}
        </div>
        <div style={{ fontSize:12, color:"#6b7280", fontWeight:800, marginLeft:12 }}>Effective (used)</div>
        <div style={{ fontSize:22, fontWeight:900, color:"#111827" }}>{effectiveScore(linked).toFixed(2)}</div>
      </div>

      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"separate", borderSpacing:0, fontSize:12 }}>
          <thead>
            <tr style={{ background:"#f8fafc" }}>
              <th style={thStyle}>Factor</th>
              <th style={thStyle}>Raw</th>
              <th style={thStyle}>Normalized (0–1)</th>
              <th style={thStyle}>Weight</th>
              <th style={thStyle}>Contribution</th>
            </tr>
          </thead>
          <tbody>
            {[
              { k:"rating",    label:"Ratings (avg /5)",            raw: linked.ratingAvg ?? 0,                 norm: exp.norm.R_rating,    w:0.30, c:exp.parts.rating },
              { k:"completed", label:"Completed appointments",       raw: linked.stats?.completedAppts ?? 0,     norm: exp.norm.R_completed, w:0.10, c:exp.parts.completed },
              { k:"research",  label:"Research engagements score",   raw: linked.stats?.researchEngagements ?? 0,norm: exp.norm.R_research,  w:0.05, c:exp.parts.research },
              { k:"years",     label:"Years of experience",          raw: linked.experienceYears ?? 0,           norm: exp.norm.R_years,     w:0.10, c:exp.parts.years },
              { k:"profile",   label:"Profile completeness (0–1)",   raw: linked.profileCompletePct ?? 0,        norm: exp.norm.R_profile,   w:0.10, c:exp.parts.profile },
              { k:"total",     label:"Total appointments",           raw: linked.stats?.totalAppts ?? 0,         norm: exp.norm.R_total,     w:0.20, c:exp.parts.total },
              { k:"others",    label:"Others (verified + followers)",raw: `verified=${!!linked.verified}, followers=${linked.followerCount ?? 0}`,
                norm: "", w:0.15, c:exp.parts.others }
            ].map(row => (
              <tr key={row.k} style={{ borderTop:"1px solid #f3f4f6" }}>
                <td style={tdStyle}>{row.label}</td>
                <td style={tdStyle}>{row.raw}</td>
                <td style={tdStyle}>{row.norm === "" ? "—" : Number(row.norm).toFixed(3)}</td>
                <td style={tdStyle}>{(row.w*100).toFixed(0)}%</td>
                <td style={{ ...tdStyle, fontWeight:800 }}>{(row.c*100).toFixed(2)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={4} style={{ ...tdStyle, textAlign:"right", fontWeight:900 }}>Explained total</td>
              <td style={{ ...tdStyle, fontWeight:900 }}>{exp.score.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {typeof linked.paraRankscore === "number" && (
        <div style={{ marginTop:8, fontSize:12, color:"#9a3412", background:"#fff7ed",
                      border:"1px solid #fed7aa", padding:8, borderRadius:8 }}>
          Sponsored override is active. Users see the <strong>Effective</strong> score ({effectiveScore(linked).toFixed(2)}).
        </div>
      )}
    </div>
  );
})()}


          {/* Actions */}
          <div style={{
            display: "flex", gap: 10, borderTop: "1px solid #f3f4f6", paddingTop: 12, marginTop: 12,
            justifyContent: showActions ? "flex-start" : "flex-end"
          }}>
            {showActions && (
              <>
                <button
                  onClick={() => handleUpdateStatus(doctor.id, "approved")}
                  style={{ padding: "10px 14px", background: "#10b981", color: "#fff", border: 0, borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
                >
                  <CheckCircle size={14} style={{ verticalAlign: "text-bottom", marginRight: 6 }} />
                  Approve
                </button>
                <button
                  onClick={() => handleUpdateStatus(doctor.id, "rejected")}
                  style={{ padding: "10px 14px", background: "#ef4444", color: "#fff", border: 0, borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
                >
                  <XCircle size={14} style={{ verticalAlign: "text-bottom", marginRight: 6 }} />
                  Reject
                </button>
              </>
            )}
            <button
              onClick={() => setDeleteConfirm(doctor.id)}
              style={{ padding: "8px 12px", background: "transparent", color: "#dc2626", border: "1px solid #dc2626", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
            >
              <Trash2 size={14} style={{ verticalAlign: "text-bottom", marginRight: 6 }} />
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ===== Loading/guard ===== */
  if (checkingRole) {
    return (
      <div style={{ display: "grid", placeItems:"center", minHeight:"100vh", color:"#6b7280" }}>
        Checking access…
      </div>
    );
  }
  if (!isAdmin) return null;
const saveDoctorCurrency = async (uid, currency) => {
  const c = String(currency || "").toUpperCase();
  if (!["ALL","EUR"].includes(c)) { alert("Currency must be ALL or EUR"); return; }
  await updateDoc(doc(db, "doctors", uid), { currency: c, rankingUpdatedAt: serverTimestamp() });
  setDoctorsById(prev => ({ ...prev, [uid]: { ...prev[uid], currency: c } }));
  alert(`Currency set to ${c}`);
};

  /* ===== Exports ===== */
  const exportVerifications = () => {
    const rows = [...pending, ...approved, ...rejected].map(v => ({
      id: v.id,
      status: v.status,
      doctorEmail: v.doctorEmail || "",
      name: v.profileData?.name || "",
      specialty: v.profileData?.specialty || "",
      createdAt: v.createdAt?.toDate?.()?.toISOString?.() || ""
    }));
    downloadCSV(rows, "verifications.csv");
  };
  const exportRecentPatients = () => {
    const rows = stats.recentPatients.map(p => ({
      id: p.id, name: p.name || "", email: p.email || "", createdAt: p.createdAt?.toDate?.()?.toISOString?.() || ""
    }));
    downloadCSV(rows, "recent_patients.csv");
  };
  const exportTopPatients = () => {
    const rows = stats.topPatients.map(p => ({
      patientId: p.patientId, name: p.name || "", email: p.email || "", appointmentCount: p.count
    }));
    downloadCSV(rows, "top_patients.csv");
  };
  const exportRevenueByDoctor = () => {
    const rows = stats.revenue.byDoctor.map(r => ({
      doctorId: r.doctorId, name: r.name || "", completedAppointments: r.completedCount, revenue: r.revenue
    }));
    downloadCSV(rows, "revenue_by_doctor.csv");
  };
  const exportServicesCatalog = () => {
    const rows = stats.services.catalog.map(s => ({
      doctorId: s.doctorId,
      doctorName: s.doctorName,
      title: s.title,
      description: s.description,
      price: s.price ?? "",
       currency: s.currency || "",
    }));
    downloadCSV(rows, "services_catalog.csv");
  };
 const exportDoctorRankings = () => {
  const rows = Object.values(doctorsById).map(d => {
    const eff = effectiveScore(d);
    return {
      doctorId: d.id,
      name: d.name || d.profile?.name || "",
      specialty: d.specialty || d.profile?.specialty || "",
      rankScore_server: typeof d.rankScore === "number" ? Number(d.rankScore).toFixed(2) : "",
      paraRankscore: typeof d.paraRankscore === "number" ? d.paraRankscore : "",
      effectiveScore: typeof eff === "number" ? eff.toFixed(2) : "",
      followers: d.followerCount ?? d.followers ?? d.metrics?.followers ?? "",
      completedAppts: d.stats?.completedAppts ?? d.completedCount ?? d.metrics?.completedCount ?? "",
      totalAppts: d.stats?.totalAppts ?? d.appointmentsCount ?? d.metrics?.appointments ?? "",
      ratingAvg: d.ratingAvg ?? d.rating ?? ""
    };
  });
  downloadCSV(rows, "doctor_rankings.csv");
};


  /* ===== Weekly trend mini chart (inline SVG) ===== */
  const Chart = ({ data }) => {
    const W = 520, H = 120, pad = 18;
    const values = data.map(d => d.value);
    const maxV = Math.max(1, ...values);
    const stepX = (W - pad * 2) / (data.length - 1 || 1);
    const pts = data.map((d, i) => {
      const x = pad + i * stepX;
      const y = pad + (H - pad * 2) * (1 - d.value / maxV);
      return [x, y];
    });
    const path = pts.map(([x,y],i) => (i? "L":"M") + x + " " + y).join(" ");
    return (
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Appointments per week">
        <rect x="0" y="0" width={W} height={H} fill="#ffffff" />
        {[0.25, 0.5, 0.75].map((r, i) => (
          <line key={i} x1="18" x2={W-18} y1={pad + (H - pad*2)*r} y2={pad + (H - pad*2)*r} stroke="#eef2f7" />
        ))}
        <path d={path} fill="none" stroke="#3b82f6" strokeWidth="2.5" />
        {pts.map(([x,y],i) => <circle key={i} cx={x} cy={y} r="3" fill="#3b82f6" />)}
      </svg>
    );
  };

  const statusBadge = (label, value, bg, fg) => (
    <div style={{ background:bg, color:fg, border:"1px solid rgba(0,0,0,.06)", padding:"10px 12px", borderRadius:12 }}>
      <div style={{ fontSize:12, color:"rgba(17,24,39,.7)", fontWeight:700 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:800 }}>{value}</div>
    </div>
  );

  /* ====================== Render ====================== */
  return (
    <div style={{ minHeight:"100vh", background:"#f9fafb", padding:24 }}>
      <div style={{ maxWidth:1200, margin:"0 auto" }}>
        {/* Header Row */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, gap:12, flexWrap:"wrap" }}>
          <div>
            <h1 style={{ margin:0, fontSize:28, fontWeight:800, color:"#111827", display:"flex", alignItems:"center", gap:10 }}>
              <Shield color="#3b82f6" size={28} /> Admin Dashboard
            </h1>
            <div style={{ color:"#6b7280", fontWeight:600 }}>
              Manage doctor verifications, profiles, services, rankings, and platform health
            </div>
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <button onClick={exportVerifications} style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"10px 12px", border:"1px solid #d1d5db", borderRadius:10, background:"#fff", fontWeight:700, cursor:"pointer" }}>
              <Download size={16} /> Verifications
            </button>
            <button onClick={exportRecentPatients} style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"10px 12px", border:"1px solid #d1d5db", borderRadius:10, background:"#fff", fontWeight:700, cursor:"pointer" }}>
              <Download size={16} /> Recent Patients
            </button>
            <button onClick={exportTopPatients} style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"10px 12px", border:"1px solid #d1d5db", borderRadius:10, background:"#fff", fontWeight:700, cursor:"pointer" }}>
              <Download size={16} /> Top Patients
            </button>
            <button onClick={exportRevenueByDoctor} style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"10px 12px", border:"1px solid #d1d5db", borderRadius:10, background:"#fff", fontWeight:700, cursor:"pointer" }}>
              <Download size={16} /> Revenue by Doctor
            </button>
            <button onClick={exportServicesCatalog} style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"10px 12px", border:"1px solid #d1d5db", borderRadius:10, background:"#fff", fontWeight:700, cursor:"pointer" }}>
              <Download size={16} /> Services Catalog
            </button>
            <button onClick={exportDoctorRankings} style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"10px 12px", border:"1px solid #d1d5db", borderRadius:10, background:"#fff", fontWeight:800, cursor:"pointer" }}>
              <Download size={16} /> Doctor Rankings
            </button>
            <button
  onClick={() => navigate("/admin-servicemapper")}
  style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid #1d4ed8",
    borderRadius: 10,
    background: "#1d4ed8",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer"
  }}
>
  <Stethoscope size={16} /> Service Mapper
</button>
            <button
  onClick={() => navigate("/admin/promos")}
  style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid #1d4ed8",
    borderRadius: 10,
    background: "#1d4ed8",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer"
  }}
>
  <Stethoscope size={16} /> Promo Manager
</button>
   <button
  onClick={() => navigate("/admin-specialities")}
  style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid #1d4ed8",
    borderRadius: 10,
    background: "#1d4ed8",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer"
  }}
>
  <Stethoscope size={16} /> Specialities Mapper
</button>

            {/* Logout */}
            <button
              onClick={handleLogout}
              title="Sign out"
              style={{
                display:"inline-flex", alignItems:"center", gap:8,
                padding:"10px 12px",
                border:"1px solid #ef4444",
                borderRadius:10,
                background:"#ef4444",
                color:"#fff",
                fontWeight:800,
                cursor:"pointer"
              }}
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        {/* Quick Actions: Rankings & Reconciliation */}
        <div style={{ display:"grid", gridTemplateColumns:"1.2fr 1fr", gap:16, marginBottom:16 }}>
          <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:16, padding:14 }}>
            <div style={{ fontWeight:800, color:"#111827", marginBottom:10, display:"flex", alignItems:"center", gap:8 }}>
              <BarChart2 size={18} /> Doctor Rankings
            </div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              <button
                onClick={recomputeAllRankings}
                style={{ padding:"10px 12px", border:"1px solid #3b82f6", background:"#3b82f6", color:"#fff", borderRadius:10, fontWeight:800, display:"inline-flex", alignItems:"center", gap:8 }}
              >
                <RefreshCcw size={16} /> Recompute all rankings
              </button>
              <div style={{ color:"#6b7280", fontSize:12 }}>
                Uses rating, completed visits, posts, years, profile completeness, total appointments, verification, followers.
              </div>
            </div>
          </div>

          <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:16, padding:14 }}>
            <div style={{ fontWeight:800, color:"#111827", marginBottom:10, display:"flex", alignItems:"center", gap:8 }}>
              <AlertTriangle size={18} color="#f59e0b" /> Doctors ↔ Verifications
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <SimpleBadge label="Approved but missing in doctors" value={recon.approvedNotInDoctors.length} bg="#fff7ed" border="#fed7aa" color="#9a3412" />
              <SimpleBadge label="Doctors without approval" value={recon.doctorsNotVerified.length} bg="#fef2f2" border="#fecaca" color="#991b1b" />
            </div>
            {(recon.approvedNotInDoctors.length > 0 || recon.doctorsNotVerified.length > 0) && (
              <div style={{ marginTop:10, display:"grid", gap:8 }}>
                {!!recon.approvedNotInDoctors.length && (
                  <Panel title="Approved but not in doctors (IDs)">
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      {recon.approvedNotInDoctors.map(id => (
                        <code key={id} style={{ background:"#f3f4f6", padding:"4px 8px", borderRadius:6 }}>{id}</code>
                      ))}
                    </div>
                  </Panel>
                )}
                {!!recon.doctorsNotVerified.length && (
                  <Panel title="Doctors without approval (IDs)">
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      {recon.doctorsNotVerified.map(id => (
                        <a key={id} href={`https://mikdok.netlify.app/doctor/${id}`} target="_blank" rel="noreferrer"
                           style={{ background:"#eef2ff", padding:"4px 8px", borderRadius:6, textDecoration:"none", color:"#3730a3", fontWeight:700 }}>
                          {id}
                        </a>
                      ))}
                    </div>
                  </Panel>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Queue Stats */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(250px, 1fr))", gap:20, marginBottom:16 }}>
          <div style={{ background:"#fff", padding:20, borderRadius:16, border:"1px solid #fbbf24" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ color:"#6b7280", fontWeight:700, fontSize:12 }}>Pending Reviews</div>
                <div style={{ color:"#f59e0b", fontWeight:800, fontSize:30 }}>{pending.length}</div>
              </div>
              <Clock size={28} color="#f59e0b" />
            </div>
          </div>
          <div style={{ background:"#fff", padding:20, borderRadius:16, border:"1px solid #10b981" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ color:"#6b7280", fontWeight:700, fontSize:12 }}>Approved Doctors</div>
                <div style={{ color:"#10b981", fontWeight:800, fontSize:30 }}>{approved.length}</div>
              </div>
              <CheckCircle size={28} color="#10b981" />
            </div>
          </div>
          <div style={{ background:"#fff", padding:20, borderRadius:16, border:"1px solid #ef4444" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ color:"#6b7280", fontWeight:700, fontSize:12 }}>Rejected Applications</div>
                <div style={{ color:"#ef4444", fontWeight:800, fontSize:30 }}>{rejected.length}</div>
              </div>
              <XCircle size={28} color="#ef4444" />
            </div>
          </div>
        </div>
 {/* More admin stats / deep dive */}
        <AdminStatsPanel stats={stats} doctorsById={doctorsById} />
        {/* System Analytics (incl revenue + services) */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))", gap:12, marginBottom: 16 }}>
          <InfoCard label="Total Patients" value={stats.patientsCount} />
          <InfoCard label="Total Appointments" value={stats.totalAppointments} />
          <InfoCard label="Active Patients (30d)" value={stats.activePatients30d} />
          <InfoCard label="Storage Used*" value={fmtBytes(stats.storageBytes)} sub="* from uploads.sizeBytes" />
          <InfoCard label="Total Services" value={stats.services.total} />
          <InfoCard label="Total Revenue*" value={fmtCurrency(stats.revenue.total)} sub="* completed appointments in fetched set" iconLeft={<DollarSign size={16} color="#16a34a" />} />
          <InfoCard label="Avg Rev / Completed" value={fmtCurrency(stats.revenue.avgPerCompleted)} />
        </div>
       

        {/* Appointment breakdown + Growth + Trend + Revenue 30d */}
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1.4fr", gap:16, marginBottom:20 }}>
          {/* Left: Trend */}
          <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:16, overflow:"hidden" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", borderBottom:"1px solid #f3f4f6" }}>
              <div style={{ fontWeight:800, color:"#111827", display:"flex", alignItems:"center", gap:8 }}>
                <BarChart2 size={18} /> Appointments (last 8 weeks)
              </div>
            </div>
            <div style={{ padding:12 }}>
              <Chart data={stats.weeklyBuckets} />
              <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginTop:8, color:"#6b7280", fontSize:12 }}>
                {stats.weeklyBuckets.map(b => (
                  <div key={b.label} style={{ background:"#f3f4f6", border:"1px solid #e5e7eb", padding:"4px 8px", borderRadius:999 }}>
                    {b.label}: <strong style={{ color:"#111827" }}>{b.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Status + Growth + Revenue 30d */}
          <div style={{ display:"grid", gridTemplateRows:"auto auto", gap:16 }}>
            <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:16, padding:14 }}>
              <div style={{ fontWeight:800, color:"#111827", marginBottom:10, display:"flex", alignItems:"center", gap:8 }}>
                <Activity size={18} /> Status Breakdown
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:10 }}>
                {statusBadge("Pending",   stats.apptStatus.pending,   "#fff7ed", "#9a3412")}
                {statusBadge("Confirmed", stats.apptStatus.confirmed, "#eff6ff", "#1d4ed8")}
                {statusBadge("Completed", stats.apptStatus.completed, "#ecfdf5", "#166534")}
                {statusBadge("Cancelled", stats.apptStatus.cancelled, "#fef2f2", "#991b1b")}
              </div>
            </div>

            <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:16, padding:14 }}>
              <div style={{ fontWeight:800, color:"#111827", marginBottom:10 }}>30d Growth & Revenue</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:12 }}>
                <SimpleBadge label="Active Patients (30d)" value={stats.activePatients30d} />
                <SimpleBadge label="Patients Δ%" value={`${stats.growth.deltaPct >= 0 ? "+" : ""}${stats.growth.deltaPct.toFixed(1)}%`} bg="#ecfdf5" border="#bbf7d0" color="#166534" />
                <SimpleBadge label="Revenue 30d" value={fmtCurrency(stats.revenue.total30d)} bg="#f0fdf4" border="#86efac" color="#166534" />
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
                <SimpleBadge label="Prev 30d Rev" value={fmtCurrency(stats.revenue.prev30d)} bg="#fff1f2" border="#fecdd3" color="#9f1239" />
                <SimpleBadge label="Δ Revenue 30d" value={`${stats.revenue.deltaPct30d >= 0 ? "+" : ""}${stats.revenue.deltaPct30d.toFixed(1)}%`} bg="#eef2ff" border="#c7d2fe" color="#3730a3" />
                <SimpleBadge label="Avg / Completed" value={fmtCurrency(stats.revenue.avgPerCompleted)} />
              </div>
            </div>
          </div>
        </div>


        {/* Monitoring: Top revenue doctors + Top doctors by volume + Top patients */}
        <div style={{ display:"grid", gridTemplateColumns:"1.2fr 1fr 1fr", gap:16, marginBottom:24 }}>
          <Panel title={<><DollarSign size={16} color="#16a34a" /> Top Revenue (all fetched)</>}>
            {!stats.revenue.topRevenueDoctors.length ? (
              <div style={{ color:"#6b7280" }}>No data yet.</div>
            ) : (
              <ul style={{ listStyle:"none", padding:0, margin:0, display:"grid", gap:8 }}>
                {stats.revenue.topRevenueDoctors.map(d => (
                  <li key={d.doctorId} style={{
                    display:"grid", gridTemplateColumns:"1fr auto auto", alignItems:"center",
                    gap:10, border:"1px solid #eee", borderRadius:10, padding:"10px 12px"
                  }}>
                    <div style={{ fontWeight:800, color:"#111827", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {d.name || d.doctorId}
                    </div>
                    <div style={{ fontSize:12, fontWeight:800, color:"#059669", background:"#dcfce7", padding:"4px 8px", borderRadius:999 }}>
                      {d.completedCount} done
                    </div>
                    <div style={{ fontWeight:900, color:"#111827" }}>{fmtCurrency(d.revenue)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
<Panel title="Top by Rank">
  {(() => {
    const list = Object.values(doctorsById)
      .filter(d => typeof effectiveScore(d) === "number")
      .sort((a,b) => effectiveScore(b) - effectiveScore(a))
      .slice(0, 8);

    if (!list.length) return <div style={{ color:"#6b7280" }}>No ranking data yet.</div>;

    return (
      <ul style={{ listStyle:"none", padding:0, margin:0, display:"grid", gap:8 }}>
        {list.map(d => (
          <li key={d.id} style={{
            display:"grid", gridTemplateColumns:"1fr auto", alignItems:"center",
            gap:10, border:"1px solid #eee", borderRadius:10, padding:"10px 12px"
          }}>
            <div style={{ minWidth:0 }}>
              <div style={{ fontWeight:800, color:"#111827", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                {d.name || d.profile?.name || d.id}
              </div>
              <div style={{ fontSize:12, color:"#6b7280" }}>
                {d.specialty || d.profile?.specialty || "—"}
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              {typeof d.paraRankscore === "number" && (
                <span title="Sponsored"
                  style={{ fontSize:10, fontWeight:900, color:"#9a3412", background:"#fff7ed", padding:"2px 6px", borderRadius:999 }}>
                  S
                </span>
              )}
              <span style={{ fontSize:12, fontWeight:900, color:"#3730a3", background:"#eef2ff", padding:"4px 8px", borderRadius:999 }}>
                {effectiveScore(d).toFixed(2)}
              </span>
            </div>
          </li>
        ))}
      </ul>
    );
  })()}
</Panel>



          <Panel title="Top Doctors (completed in 30d)">
            {!stats.topDoctors.length ? (
              <div style={{ color:"#6b7280" }}>No data yet.</div>
            ) : (
              <ul style={{ listStyle:"none", padding:0, margin:0, display:"grid", gap:8 }}>
                {stats.topDoctors.map(d => (
                  <li key={d.doctorId} style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    border:"1px solid #eee", borderRadius:10, padding:"10px 12px"
                  }}>
                    <div
  style={{
    fontWeight: 800,
    color: "#1d4ed8",
    cursor: "pointer",
    textDecoration: "underline"
  }}
  onClick={() => openDoctorServices(d)}
>
  {d.name}
</div>

                    <div style={{ background:"#dbeafe", color:"#1e40af", fontWeight:800, fontSize:12, padding:"4px 8px", borderRadius:999 }}>
                      {d.count}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Top Patients (recent)">
            {!stats.topPatients.length ? (
              <div style={{ color:"#6b7280" }}>No data yet.</div>
            ) : (
              <ul style={{ listStyle:"none", padding:0, margin:0, display:"grid", gap:8 }}>
                {stats.topPatients.map(tp => (
                  <li key={tp.patientId} style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    border:"1px solid #eee", borderRadius:10, padding:"10px 12px"
                  }}>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontWeight:800, color:"#111827", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                        {tp.name || "Patient"}
                      </div>
                      <div style={{ color:"#6b7280", fontSize:12, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                        {tp.email || tp.patientId}
                      </div>
                    </div>
                    <span style={{ fontSize:12, fontWeight:800, color:"#059669", background:"#dcfce7", padding:"4px 8px", borderRadius:999 }}>
                      {tp.count}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
             {isAdmin && (
  <AdminPromoManager isAdmin={isAdmin} />
)}



{/* === ALL PATIENTS === */}
<section className="admin-card" style={{ marginTop: 24 }}>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 12,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Users size={18} />
      <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
        Të gjithë pacientët ({allPatients.length})
      </h2>
    </div>

    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        type="text"
        placeholder="Kërko me emër / email / telefon…"
        value={patientSearch}
        onChange={(e) => setPatientSearch(e.target.value)}
        style={{
          padding: "6px 10px",
          borderRadius: 8,
          border: "1px solid #e5e7eb",
          fontSize: 13,
          minWidth: 220,
        }}
      />
    </div>
  </div>

  {patientsLoading ? (
    <div style={{ padding: 12, fontSize: 14 }}>Duke ngarkuar pacientët…</div>
  ) : !filteredPatients.length ? (
    <div style={{ padding: 12, fontSize: 14 }}>Asnjë pacient nuk u gjet.</div>
  ) : (
    <div
      style={{
        maxHeight: 360,
        overflowY: "auto",
        borderRadius: 12,
        border: "1px solid #e5e7eb",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13,
        }}
      >
        <thead
          style={{
            background: "#f9fafb",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <tr>
            <th style={{ textAlign: "left", padding: "8px 10px" }}>Emri</th>
            <th style={{ textAlign: "left", padding: "8px 10px" }}>Email</th>
            <th style={{ textAlign: "left", padding: "8px 10px" }}>Telefon</th>
            <th style={{ textAlign: "left", padding: "8px 10px" }}>Krijuar</th>
            <th style={{ textAlign: "left", padding: "8px 10px" }}>Status</th>
            <th style={{ textAlign: "left", padding: "8px 10px" }}>Actions</th>

          </tr>
        </thead>
                <tbody>
          {filteredPatients.map((p) => (
            <tr key={p.id} style={{ borderTop: "1px solid #f3f4f6" }}>
              <td style={{ padding: "8px 10px" }}>{p.name || "—"}</td>
              <td style={{ padding: "8px 10px" }}>{p.email || "—"}</td>
              <td style={{ padding: "8px 10px" }}>{p.phone || "—"}</td>
              <td style={{ padding: "8px 10px" }}>{p.createdAt || "—"}</td>
              <td style={{ padding: "8px 10px" }}>
                <span
                  style={{
                    fontSize: 11,
                    padding: "2px 8px",
                    borderRadius: 999,
                    backgroundColor: p.isActive ? "#dcfce7" : "#fee2e2",
                    color: p.isActive ? "#166534" : "#991b1b",
                  }}
                >
                  {p.isActive ? "Aktiv" : "Çaktivizuar"}
                </span>
              </td>
              <td style={{ padding: "8px 10px" }}>
                <button
                  onClick={() => openPatientActivity(p)}
                  style={{
                    fontSize: 11,
                    padding: "4px 8px",
                    borderRadius: 999,
                    border: "1px solid #e5e7eb",
                    background: "#f9fafb",
                    cursor: "pointer",
                  }}
                >
                  Shiko aktivitetin
                </button>
              </td>
            </tr>
          ))}
        </tbody>

      </table>
    </div>
  )}
</section>
{/* === PATIENT ACTIVITY PANEL === */}
{activityOpen && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,0.45)",
      display: "flex",
      justifyContent: "flex-end",
      zIndex: 50,
    }}
    onClick={() => setActivityOpen(false)}
  >
    <div
      style={{
        width: "420px",
        maxWidth: "100%",
        height: "100%",
        background: "white",
        padding: 16,
        boxShadow: "0 20px 40px rgba(15,23,42,0.25)",
        overflowY: "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Pacient</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {activityPatient?.name || "—"}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            {activityPatient?.email}
          </div>
        </div>
        <button
          onClick={() => setActivityOpen(false)}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 20,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {activityLoading ? (
        <div style={{ padding: 8, fontSize: 14 }}>Duke ngarkuar aktivitetin…</div>
      ) : (
        <>
          {/* Appointments */}
          <div style={{ marginTop: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              Takime të fundit ({activityData.appointments.length})
            </h3>
            {activityData.appointments.length === 0 ? (
              <div style={{ fontSize: 12, color: "#6b7280" }}>Asnjë takim.</div>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {activityData.appointments.map((a) => (
                  <li
                    key={a.id}
                    style={{
                      padding: "6px 0",
                      borderBottom: "1px solid #f3f4f6",
                      fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {a.serviceName || "Konsultë"}
                    </div>
                    <div style={{ color: "#6b7280" }}>
                      {a.scheduledDate || "—"} • {a.scheduledTime || "—"} •{" "}
                      <span
                        style={{
                          textTransform: "capitalize",
                          color:
                            a.status === "completed"
                              ? "#16a34a"
                              : a.status === "cancelled"
                              ? "#b91c1c"
                              : "#374151",
                        }}
                      >
                        {a.status || "pending"}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Medical records */}
          <div style={{ marginTop: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              Rekorde mjekësore ({activityData.medicalRecords.length})
            </h3>
            {activityData.medicalRecords.length === 0 ? (
              <div style={{ fontSize: 12, color: "#6b7280" }}>Asnjë rekord.</div>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {activityData.medicalRecords.map((r) => (
                  <li
                    key={r.id}
                    style={{
                      padding: "6px 0",
                      borderBottom: "1px solid #f3f4f6",
                      fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {r.title || r.type || "Rekord mjekësor"}
                    </div>
                    <div style={{ color: "#6b7280" }}>
                      Doktor: {r.doctorName || r.doctorId || "—"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Documents */}
          <div style={{ marginTop: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              Dokumente të ngarkuara ({activityData.documents.length})
            </h3>
            {activityData.documents.length === 0 ? (
              <div style={{ fontSize: 12, color: "#6b7280" }}>Asnjë dokument.</div>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {activityData.documents.map((d) => (
                  <li
                    key={d.id}
                    style={{
                      padding: "6px 0",
                      borderBottom: "1px solid #f3f4f6",
                      fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {d.fileName || d.title || "Dokument"}
                    </div>
                    <div style={{ color: "#6b7280" }}>
                      {d.contentType || ""} • {fmtBytes(d.size || d.sizeBytes)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Reviews */}
          <div style={{ marginTop: 16, marginBottom: 24 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              Reviews nga ky pacient ({activityData.reviews.length})
            </h3>
            {activityData.reviews.length === 0 ? (
              <div style={{ fontSize: 12, color: "#6b7280" }}>Asnjë review.</div>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {activityData.reviews.map((rv) => (
                  <li
                    key={rv.id}
                    style={{
                      padding: "6px 0",
                      borderBottom: "1px solid #f3f4f6",
                      fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {rv.doctorName || rv.doctorId || "Doktor"}
                    </div>
                    <div style={{ color: "#ca8a04" }}>
                      ⭐ {rv.rating || rv.score || "-"}
                    </div>
                    <div style={{ color: "#6b7280" }}>
                      {rv.comment?.slice(0, 120) || "Pa koment" }
                      {rv.comment && rv.comment.length > 120 ? "…" : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  </div>
)}


 


        {/* Tabs */}
        <div style={{
          background:"#fff", borderRadius:16, padding:8, border:"1px solid #e5e7eb",
          display:"flex", gap:6, marginBottom:12
        }}>
          {[
            { key: "pending",  label: "Pending",   count: pending.length,  color: "#f59e0b" },
            { key: "approved", label: "Approved",  count: approved.length, color: "#10b981" },
            { key: "rejected", label: "Rejected",  count: rejected.length, color: "#ef4444" }
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                flex: 1, padding:"10px 12px", border:"1px solid #e5e7eb",
                background: activeTab === t.key ? t.color : "#fff",
                color: activeTab === t.key ? "#fff" : "#6b7280",
                borderRadius:10, fontWeight:800, cursor:"pointer"
              }}
            >
              {t.label} ({t.count})
            </button>
          ))}
        </div>

        {/* Search + bulk (pending) */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, marginBottom:12, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, background:"#fff", border:"1px solid #e5e7eb", padding:"8px 10px", borderRadius:10, flex: "1 1 280px" }}>
            <Search size={16} color="#6b7280" />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={`Search ${activeTab} by name, email, specialty…`}
              style={{ border:0, outline:0, width:"100%", background:"transparent" }}
            />
          </div>

          {activeTab === "pending" && (
            <div style={{ display:"flex", gap:8 }}>
              <button
                onClick={approveAllPending}
                disabled={!pending.length}
                style={{
                  padding:"10px 12px", border:"1px solid #10b981", background:"#10b981",
                  color:"#fff", borderRadius:10, fontWeight:800, cursor: pending.length ? "pointer" : "not-allowed"
                }}
                title="Approve all pending verifications"
              >
                Approve All
              </button>
              <button
                onClick={rejectSelected}
                disabled={!selectedIds.size}
                style={{
                  padding:"10px 12px", border:"1px solid #ef4444", background:"#ef4444",
                  color:"#fff", borderRadius:10, fontWeight:800, cursor: selectedIds.size ? "pointer" : "not-allowed"
                }}
                title="Reject selected"
              >
                Reject Selected
              </button>
            </div>
          )}
        </div>

        {/* Lists */}
        <div>
          {activeTab === "pending" && (
            <ListOrEmpty
              emptyText="No pending verifications match your search."
              items={filteredList}
              renderItem={(v) => <Card key={v.id} doctor={v} showActions />}
            />
          )}
          {activeTab === "approved" && (
            <ListOrEmpty
              emptyText="No approved doctors match your search."
              items={filteredList}
              renderItem={(v) => <Card key={v.id} doctor={v} />}
            />
          )}
          {activeTab === "rejected" && (
            <ListOrEmpty
              emptyText="No rejected applications match your search."
              items={filteredList}
              renderItem={(v) => <Card key={v.id} doctor={v} />}
            />
          )}
        </div>

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", display:"grid", placeItems:"center", zIndex:1000 }}>
            <div style={{ background:"#fff", borderRadius:20, padding:24, width:"min(420px, 92vw)", boxShadow:"0 25px 50px -12px rgba(0,0,0,0.25)" }}>
              <div style={{ textAlign:"center", marginBottom:16 }}>
                <div style={{ width:64, height:64, borderRadius:"50%", background:"#fee2e2", display:"grid", placeItems:"center", margin:"0 auto 12px" }}>
                  <Trash2 size={26} color="#dc2626" />
                </div>
                <div style={{ fontWeight:800, fontSize:18, color:"#111827" }}>Delete Doctor Profile</div>
                <div style={{ color:"#6b7280", marginTop:6 }}>
                  Permanently delete verification & profile. This cannot be undone.
                </div>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  style={{ flex:1, padding:"10px 12px", border:"1px solid #d1d5db", background:"#f3f4f6", borderRadius:10, fontWeight:800 }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const d = [...pending, ...approved, ...rejected].find(v => v.id === deleteConfirm);
                    const uid = d?.profileData?.uid;
                    handleDeleteProfile(deleteConfirm, uid);
                  }}
                  style={{ flex:1, padding:"10px 12px", border:"1px solid #dc2626", background:"#dc2626", color:"#fff", borderRadius:10, fontWeight:800 }}
                >
                  Delete Profile
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      {/* === Doctor Service Mapping Modal === */}
{serviceModalOpen && activeDoctor && (
  <div style={{
    position:"fixed", inset:0, background:"rgba(0,0,0,.4)",
    display:"grid", placeItems:"center", zIndex:1000
  }}>
    <div style={{
      background:"#fff", borderRadius:20, padding:24,
      width:"min(600px, 92vw)", maxHeight:"90vh", overflowY:"auto",
      boxShadow:"0 25px 50px rgba(0,0,0,0.2)"
    }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <h2 style={{ margin:0, fontWeight:800, color:"#111827" }}>
          {activeDoctor.name} – Services
        </h2>
        <button onClick={()=>setServiceModalOpen(false)} style={{
          background:"transparent", border:"none", cursor:"pointer", fontSize:18
        }}>✕</button>
      </div>

      {activeServices.length === 0 ? (
        <div style={{ color:"#6b7280" }}>No services found for this doctor.</div>
      ) : (
        <div style={{ display:"grid", gap:14 }}>
          {activeServices.map((srv, i) => (
            <div key={i} style={{
              border:"1px solid #e5e7eb", borderRadius:10, padding:14
            }}>
              <div style={{ fontWeight:800, color:"#111827" }}>{srv.title}</div>
              <div style={{ fontSize:12, color:"#6b7280", marginBottom:6 }}>
                {srv.price != null ? viewPrice(srv.price, srv.currency) : "—"}
              </div>
              <input
                defaultValue={(srv.keywords || []).join(", ")}
                placeholder="Keywords (comma separated)"
                onChange={(e)=>{
                  const kw = e.target.value.split(",").map(x=>x.trim()).filter(Boolean);
                  setActiveServices(prev=>{
                    const next=[...prev];
                    next[i].keywords=kw;
                    return next;
                  });
                }}
                onBlur={(e)=>{
                  const kw = e.target.value.split(",").map(x=>x.trim()).filter(Boolean);
                  saveServiceKeywords(activeDoctor.id, i, kw);
                }}
                style={{
                  width:"100%", border:"1px solid #d1d5db", borderRadius:8,
                  padding:"8px 10px", fontSize:13
                }}
              />
              <div style={{ fontSize:12, marginTop:4, color:"#6b7280" }}>
                {srv.keywords && srv.keywords.length
                  ? <span style={{ color:"#16a34a", fontWeight:700 }}>✅ Mapped</span>
                  : <span style={{ color:"#dc2626", fontWeight:700 }}>❌ Missing</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
)}

    </div>
    
  );
};

/* ====================== Small UI helpers ====================== */
const ListOrEmpty = ({ emptyText, items, renderItem }) => {
  if (!items || !items.length) {
    return (
      <div style={{ background:"#fff", border:"2px dashed #e5e7eb", borderRadius:16, padding:40, textAlign:"center", color:"#6b7280" }}>
        {emptyText}
      </div>
    );
  }
  return <>{items.map(renderItem)}</>;
};

const Panel = ({ title, children }) => (
  <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:16, overflow:"hidden" }}>
    <div style={{ padding:"10px 14px", borderBottom:"1px solid #f3f4f6", fontWeight:800, color:"#111827", display:"flex", alignItems:"center", gap:8 }}>
      {typeof title === "string" ? <>{title}</> : title}
    </div>
    <div style={{ padding:12 }}>{children}</div>
  </div>
);

const InfoCard = ({ label, value, sub, iconLeft }) => (
  <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:14, display:"grid", gap:4, alignContent:"start" }}>
    <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#6b7280", fontWeight:700 }}>
      {iconLeft} {label}
    </div>
    <div style={{ fontSize:22, fontWeight:800 }}>{value}</div>
    {sub && <div style={{ fontSize:11, color:"#9ca3af" }}>{sub}</div>}
  </div>
);

const SimpleBadge = ({ label, value, bg="#f8fafc", border="#e5e7eb", color="#111827" }) => (
  <div style={{ background:bg, border:`1px solid ${border}`, padding:12, borderRadius:12 }}>
    <div style={{ fontSize:12, color: color === "#111827" ? "#6b7280" : color, fontWeight:800 }}>{label}</div>
    <div style={{ fontSize:20, fontWeight:900, color }}>{value}</div>
  </div>
);

const thStyle = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: 12,
  color: "#6b7280",
  borderBottom: "1px solid #f3f4f6",
  position: "sticky",
  top: 0,
  background: "#fafafa",
};
const tdStyle = { padding: "12px 14px", fontSize: 14, color: "#111827", verticalAlign: "top" };

export default AdminDashboard;
